import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

// Cache admin status to avoid repeated API calls
let adminCache: { userId: string; isAdmin: boolean; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useAdmin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        console.log('🔐 useAdmin: No user, setting isAdmin=false');
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Clear cache if user changed (e.g., after login/logout)
      if (adminCache && adminCache.userId !== user.id) {
        console.log('🔐 useAdmin: User changed, clearing stale cache');
        adminCache = null;
      }

      // Check cache first
      if (adminCache && 
          adminCache.userId === user.id && 
          Date.now() - adminCache.timestamp < CACHE_DURATION) {
        console.log('🔐 useAdmin: Using cached admin status:', adminCache.isAdmin);
        setIsAdmin(adminCache.isAdmin);
        setLoading(false);
        return;
      }

      console.log('🔐 useAdmin: Fetching fresh admin status for user:', user.id);

      try {
        // Prefer secure RPC to avoid potential RLS recursion issues
        const { data, error } = await supabase.rpc('get_current_user_role');
        console.log('🔐 useAdmin: RPC result:', { data, error });
        
        const userIsAdmin = data === 'admin' || data === 'staff';
        
        if (error) {
          console.warn('🔐 useAdmin: RPC failed, using fallback query:', error);
          // Fallback to direct profile query if RPC is unavailable
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('user_id', user.id)
            .maybeSingle();
          
          console.log('🔐 useAdmin: Fallback query result:', { profile, profileError });
          
          if (profileError) throw profileError;
          const fallbackIsAdmin = profile?.role === 'admin' || profile?.role === 'staff';
          
          console.log('🔐 useAdmin: Setting isAdmin (fallback):', fallbackIsAdmin);
          setIsAdmin(fallbackIsAdmin);
          
          // Cache the result
          adminCache = {
            userId: user.id,
            isAdmin: fallbackIsAdmin,
            timestamp: Date.now()
          };
        } else {
          console.log('🔐 useAdmin: Setting isAdmin (RPC):', userIsAdmin);
          setIsAdmin(userIsAdmin);
          
          // Cache the result
          adminCache = {
            userId: user.id,
            isAdmin: userIsAdmin,
            timestamp: Date.now()
          };
        }
      } catch (e) {
        console.error('🔐 useAdmin: Error checking admin status:', e);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  return { isAdmin, loading, user };
}
